// The files attached to a Case's Context or to a step's body or result: open
// one, or take it off (the file itself goes when nothing else holds it).
import type { CaseFile } from "@trameria/core/cases-shared";
import { m } from "@trameria/i18n";
import { apiErrorWithLead } from "../data/errors";
import { formatNumber } from "../time/format";
import { ErrorLine } from "../ui/bits";
import { Icon } from "../ui/icon";
import { useRemoveCaseFile } from "./queries";

const sizeText = (bytes: number) =>
  bytes < 1024
    ? m.cases_file_bytes({ n: formatNumber(bytes) })
    : bytes < 1024 * 1024
      ? m.cases_file_kb({ n: formatNumber(Math.round(bytes / 1024)) })
      : m.cases_file_mb({
          n: formatNumber(Math.round((bytes / 1024 / 1024) * 10) / 10),
        });

export function CaseFiles({
  caseId,
  files,
}: {
  caseId: string;
  files: readonly CaseFile[];
}) {
  const remove = useRemoveCaseFile();
  if (files.length === 0) return null;
  const failed =
    remove.isError && files.some((f) => f.id === remove.variables?.fileId)
      ? remove.variables
      : null;
  return (
    <div className="mt-2">
      <ul aria-label={m.cases_files()} className="flex flex-col">
        {files.map((f) => (
          <li
            key={f.id}
            className="group relative -mx-[6px] flex min-w-0 items-center gap-[8px] rounded-[6px] py-[3px] pr-[30px] pl-[6px] text-[12.5px] hover:bg-hover"
          >
            <Icon kind="attach" size={14} className="text-muted" />
            <a
              href={`/api/files/${encodeURIComponent(f.id)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 shrink truncate text-body hover:underline"
            >
              {f.name}
            </a>
            <span className="flex-none tabular-nums text-faint">
              {sizeText(f.size)}
            </span>
            <button
              type="button"
              aria-label={m.cases_file_remove({ name: f.name })}
              onClick={() =>
                remove.mutate({ caseId, fileId: f.id, name: f.name })
              }
              className="absolute top-1/2 right-[4px] inline-flex h-5 w-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded-[5px] text-muted opacity-0 hover:bg-selected hover:text-body focus:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100"
            >
              <Icon kind="close" size={11} />
            </button>
          </li>
        ))}
      </ul>
      {failed && (
        <ErrorLine>
          {apiErrorWithLead(
            remove.error,
            m.cases_file_remove_failed({ name: failed.name }),
          )}
        </ErrorLine>
      )}
    </div>
  );
}
